import { Injectable, inject } from '@angular/core';
import { GeoJSONSource, Map } from 'maplibre-gl';
import { UtilsService } from '../utils/utils.service';
import { MapSkin, SettingsService } from '../account/settings.service';
import { WaypointService } from '../core/waypoint.service';
import { DisplayMode } from '../../models/display-mode';
import { getMapSymbolScale, scaleRaster } from './map-symbol-scale';
import { MapPaletteService } from './map-palette.service';

@Injectable({
  providedIn: 'root'
})
export class CursorService {

  private cursorSource?: GeoJSONSource;
  private cursorAnimationFrame?: number;
  private mapInstance?: Map;

  /** Persistent state */
  private lastCursorLat?: number;
  private lastCursorLon?: number;
  private bearing = 0;

  /** Runtime visual truth (what is actually on screen) */
  private cursorState = {
    lon: 0,
    lat: 0,
    bearing: 0
  };

  /** Animation coordination */
  private isCursorAnimating = false;
  private pendingBearing: number | null = null;

  /** Visibility requested via hide()/show(); reapplied when the layer is recreated */
  private hidden = false;

  private readonly utilsService = inject(UtilsService);
  private readonly settingsService = inject(SettingsService);
  private readonly mapPaletteService = inject(MapPaletteService);
  private readonly waypointService = inject(WaypointService);

  constructor() {
    this.waypointService.ObservableSelectedDisplayMode.subscribe((mode) => {
      if (mode === DisplayMode.SHOW_ON_MAP) {
        this.hide();
      } else {
        this.show();
      }
    });
  }

  public isCursorInitialized(): boolean {
    // Check the live style, not just our cached reference: a setStyle() call
    // (skin change) or map re-creation removes the source while cursorSource
    // would still be truthy.
    return this.mapInstance?.getSource('cursor-point') !== undefined;
  }

  /** Drop references to a removed map so the cursor re-initializes on the next one */
  public reset(): void {
    if (this.cursorAnimationFrame) {
      cancelAnimationFrame(this.cursorAnimationFrame);
      this.cursorAnimationFrame = undefined;
    }
    this.isCursorAnimating = false;
    this.cursorSource = undefined;
    this.mapInstance = undefined;
  }

  public getBearing(): number {
    return this.bearing;
  }

  public getLastCursorPosition(): {
    longitude: number | undefined;
    latitude: number | undefined;
  } {
    return {
      longitude: this.lastCursorLon,
      latitude: this.lastCursorLat
    };
  }

  public setCursorBearing(bearing: number): void {
    this.bearing = this.utilsService.interpolateBearing(
      this.bearing,
      bearing,
      0.15
    );

    if (this.isCursorAnimating) {
      this.pendingBearing = this.bearing;
      return;
    }

    this.cursorState.bearing = this.bearing;
    this.pushCursor();
  }

  public initCursorAt(
    longitude: number,
    latitude: number,
    mapInstance: Map
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const { image, pixelRatio } = this.buildCursorImage();

        // Replace any existing image: it can survive a diffed setStyle()
        // and would otherwise keep the previous skin's icon
        if (mapInstance.hasImage('cursor-icon')) {
          mapInstance.removeImage('cursor-icon');
        }
        mapInstance.addImage('cursor-icon', image, { pixelRatio });

        if (!mapInstance.getSource('cursor-point')) {
          mapInstance.addSource('cursor-point', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: [{
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [longitude, latitude]
                },
                properties: { bearing: this.bearing }
              }]
            }
          });
        }

        if (!mapInstance.getLayer('cursor-layer')) {
          mapInstance.addLayer({
            id: 'cursor-layer',
            type: 'symbol',
            source: 'cursor-point',
            layout: {
              // The canvas is drawn at the display size × pixelRatio, so the
              // icon renders 1:1 — no icon-size scaling needed.
              'icon-image': 'cursor-icon',
              'icon-allow-overlap': true,
              'icon-rotation-alignment': 'map',
              'icon-rotate': ['get', 'bearing']
            }
          });
        }

        mapInstance.setLayoutProperty(
          'cursor-layer',
          'visibility',
          this.hidden ? 'none' : 'visible'
        );

        this.cursorSource =
          mapInstance.getSource('cursor-point') as GeoJSONSource;
        this.mapInstance = mapInstance;

        this.cursorState = { lon: longitude, lat: latitude, bearing: this.bearing };
        this.lastCursorLon = longitude;
        this.lastCursorLat = latitude;

        resolve();
      } catch (error) {
        // addSource/addLayer throw while a new style is still loading;
        // the caller retries on the next position update.
        reject(error);
      }
    });
  }

  /**
   * Draw the cursor (disc + drop shadow + arrow) onto a canvas at the device
   * pixel ratio and return raw RGBA pixels for maplibre's addImage().
   *
   * Replaces loading cursor.svg into an <img>: that raster was fixed at a 2×
   * oversample, crisp on retina @2× but visibly pixelated on @3× phones where it
   * is upsampled. Drawing on a canvas lets us rasterize at exactly the device
   * resolution, and the shadow becomes a native canvas shadow — no SVG filter
   * region clipping the disc's anti-aliased edge. The image is built once and
   * cached by maplibre as a GL texture, so per-frame cost is unchanged.
   */
  private buildCursorImage(): {
    image: { width: number; height: number; data: Uint8Array };
    pixelRatio: number;
  } {
    const DESIGN = 78; // matches the source SVG viewBox
    const skin = this.settingsService.getMap().Skin;
    const discColor = this.mapPaletteService.surface(skin === MapSkin.Dark ? '#AAAFBC' : '#ffffff');
    const arrowColor = this.mapPaletteService.accent('#151C24');

    // Rasterize at 2× the device pixel ratio. The extra 2× supersample keeps the
    // icon crisp while it animates: the cursor is placed at sub-pixel screen
    // positions every frame, and a texture at only 1× device resolution samples
    // blurry as it moves — visible on desktop, and worse under fractional
    // Windows display scaling. addImage() gets the matching pixelRatio, so the
    // on-screen size (the former scaleRaster(125) × icon-size 0.5) is unchanged
    // at every dpr; scaleRaster keeps the touch-device size bump.
    // Desktop reads a touch small; nudge it up a little. Touch (where the symbol
    // scale already bumps the size) is left exactly as-is.
    const desktopBump = getMapSymbolScale() === 1 ? 1.15 : 1;
    const dpr = Math.min(window.devicePixelRatio || 1, 4);
    const pixelRatio = 2 * dpr;
    const size = Math.round(scaleRaster(125) * desktopBump * dpr);
    const scale = size / DESIGN;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get a 2D context for the cursor icon.');
    }
    ctx.scale(scale, scale);

    // Disc with drop shadow. shadowBlur/shadowOffset are NOT affected by the
    // canvas transform, so they are scaled to device pixels by hand. blur 8 ≈
    // the SVG feGaussianBlur stdDeviation 4; offset 8 matches feOffset dy=8.
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
    ctx.shadowBlur = 8 * scale;
    ctx.shadowOffsetY = 8 * scale;
    ctx.fillStyle = discColor;
    ctx.beginPath();
    ctx.arc(39, 31, 31, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Arrow (no shadow) — the same vector path as the SVG, stroke width 1.
    const arrow = new Path2D(
      'M38.5332 11.5C38.738 11.4973 38.9238 11.6202 39.002 11.8096L52.4619 44.4102C52.5388 44.5964 52.4965 44.8102 52.3545 44.9531C52.2125 45.096 51.9982 45.1402 51.8115 45.0645L38.8936 39.8203L26.2031 45.457C26.0187 45.5389 25.8028 45.502 25.6562 45.3633C25.5097 45.2245 25.4605 45.0109 25.5322 44.8223L38.0723 11.8223L38.1045 11.7539C38.191 11.6008 38.3537 11.5026 38.5332 11.5Z'
    );
    ctx.fillStyle = arrowColor;
    ctx.strokeStyle = arrowColor;
    ctx.lineJoin = 'round';
    ctx.fill(arrow);
    ctx.stroke(arrow);

    const imageData = ctx.getImageData(0, 0, size, size);
    return {
      image: {
        width: size,
        height: size,
        data: new Uint8Array(imageData.data.buffer)
      },
      pixelRatio
    };
  }

  public hide(): void {
    this.hidden = true;
    if (this.mapInstance?.getLayer('cursor-layer')) {
      this.mapInstance.setLayoutProperty('cursor-layer', 'visibility', 'none');
    }
  }

  public show(): void {
    this.hidden = false;
    if (this.mapInstance?.getLayer('cursor-layer')) {
      this.mapInstance.setLayoutProperty('cursor-layer', 'visibility', 'visible');
    }
  }

  public animateCursorTo(
    targetLon: number,
    targetLat: number,
    bearing: number,
    duration = 400
  ): void {
    if (!this.cursorSource) return;

    /** Cancel any running animation */
    if (this.cursorAnimationFrame) {
      cancelAnimationFrame(this.cursorAnimationFrame);
      this.cursorAnimationFrame = undefined;
    }

    this.isCursorAnimating = duration > 0;

    const startLon = this.cursorState.lon;
    const startLat = this.cursorState.lat;

    const startTime = performance.now();

    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);

      const deltaLon =
        this.utilsService.shortestAngleDelta(startLon, targetLon);

      this.cursorState.lon = startLon + deltaLon * t;
      this.cursorState.lat = startLat + (targetLat - startLat) * t;

      /** Bearing comes from controller — no logic here */
      this.cursorState.bearing = bearing;

      this.pushCursor();

      if (t < 1) {
        this.cursorAnimationFrame =
          requestAnimationFrame(animate);
      } else {
        this.finishAnimation(targetLon, targetLat);
      }
    };

    if (duration > 0) {
      this.cursorAnimationFrame =
        requestAnimationFrame(animate);
    } else {
      /** Instant update */
      this.cursorState.lon = targetLon;
      this.cursorState.lat = targetLat;
      this.cursorState.bearing = bearing;
      this.pushCursor();
    }
  }

  private finishAnimation(lon: number, lat: number): void {
    this.cursorAnimationFrame = undefined;
    this.isCursorAnimating = false;

    this.cursorState.lon = lon;
    this.cursorState.lat = lat;

    this.lastCursorLon = lon;
    this.lastCursorLat = lat;

    this.pushCursor();
  }

  private pushCursor(): void {
    this.cursorSource?.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            this.cursorState.lon,
            this.cursorState.lat
          ]
        },
        properties: {
          bearing: this.cursorState.bearing
        }
      }]
    });
  }
}